import { Authentication } from '@leedpulse-monorepo/classes';
import { CustomSelect, mergeOptionsWithPinnedLabels, selectedTokens } from '@leedpulse-monorepo/components';
import { Spin, Tag, Typography } from 'antd';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import searchCompanies from '../../../../domain/use_cases/companies/searchCompanies';

type CustomSelectProps = ComponentProps<typeof CustomSelect>;

export type DebouncedCompanySelectProps = Omit<CustomSelectProps, 'options' | 'onSearch' | 'remoteSearch'> & {
  auth: Authentication | null | undefined;
  debounceTimeout?: number;
};

/**
 * CustomSelect with debounced Algolia companies search (company id as value).
 * Same pattern as DebouncedPlansSelect (remoteSearch + CustomSelect).
 */
export default function DebouncedCompanySelect({
  auth,
  debounceTimeout = 300,
  value,
  ...selectProps
}: DebouncedCompanySelectProps) {
  const [options, setOptions] = useState<{ label: string | React.ReactNode; value: string }[]>([]);
  const [fetching, setFetching] = useState(false);
  const fetchRef = useRef(0);
  const [labelByValue, setLabelByValue] = useState<Record<string, string | React.ReactNode>>({});
  const [nameByValue, setNameByValue] = useState<Record<string, string>>({});

  const selected = useMemo(() => selectedTokens(value), [value]);

  const selectOptions = useMemo(
    () => mergeOptionsWithPinnedLabels(options, selected, labelByValue),
    [options, selected, labelByValue]
  );

  const fetchOptions = useCallback((value: string) => {
    if (!auth) {
      return;
    }

    fetchRef.current += 1;
    const fetchId = fetchRef.current;

    setOptions([]);
    setFetching(true);

    searchCompanies(auth, value.trim(), 0, 20)
      .then((response) => {
        if (fetchId !== fetchRef.current) {
          return;
        }
        const names: Record<string, string> = {};
        const next = response.hits.map((company) => {
          const id = String(company.id ?? company.objectID ?? '');
          names[id] = company.name ?? id;
          return { label: <Typography.Text>{company.name} <Tag color="lightgrey">{id}</Tag></Typography.Text>, value: id };
        });
        setOptions(next);
        setNameByValue((prev) => ({ ...prev, ...names }));
        setFetching(false);
      })
      .catch((e) => {
        console.error('[DebouncedCompanySelect] searchCompanies error:', e);
        if (fetchId !== fetchRef.current) {
          return;
        }
        setOptions([]);
        setFetching(false);
      });
  }, [auth]);

  const debounceFetcher = useMemo(() => {
    // react/refs signale une closure lisant une ref passee a une fonction
    // pendant le rendu. `debounce` ne l'appelle jamais de facon synchrone :
    // elle ne s'execute qu'apres le delai, hors rendu.
    // eslint-disable-next-line react/refs
    return debounce(fetchOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  useEffect(() => {
    if (selected.length === 0) {
      // Cache de libelles, pas une derivation : la mise a jour juste en dessous lit
      // prev pour garder le libelle d'une option qui n'est plus dans la page de
      // resultats courante. Un calcul en rendu perdrait ces libelles.
      // eslint-disable-next-line react/set-state-in-effect
      setLabelByValue({});
      return;
    }
    setLabelByValue((prev) => {
      const next: Record<string, string | React.ReactNode> = {};
      for (const v of selected) {
        const opt = options.find((o) => o.value === v);
        if (opt) {
          next[v] = opt.label;
        } else if (prev[v]) {
          next[v] = prev[v];
        }
      }
      return next;
    });
  }, [selected, options]);

  useEffect(() => {
    // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
    // chargement declenche ici. Architecture Context + use case du repo.
    // eslint-disable-next-line react/set-state-in-effect
    fetchOptions('');
  }, [fetchOptions]);

  return (
    <CustomSelect
      {...selectProps}
      value={value}
      remoteSearch
      filterOption={false}
      options={selectOptions}
      labelRender={({ value, label }) => nameByValue[String(value)] ?? label}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : 'Aucun résultat'}
      loading={fetching}
    />
  );
}